/**
 * The image, video, document or place a WhatsApp template shows at the top.
 *
 * WhatsApp registers only that a template has one; every message supplies it
 * again, so Connect keeps it with the template and the template is not ready
 * until it has one. It is saved the moment it is chosen — a file is not a field
 * to hold until Save — and the server checks it is a kind WhatsApp takes there.
 */

import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FileText, ImageIcon, MapPin, Upload, Video } from 'lucide-react';
import { ApiError, Alert, Button, Input, api, toast, useFileUrl } from '@connect/web-shared';

import type { ChannelTemplate, TemplateHeader, TemplateHeaderFormat } from './types';

const ACCEPT: Record<Exclude<TemplateHeaderFormat, 'location'>, { accept: string; words: string }> = {
  image: { accept: 'image/jpeg,image/png', words: 'A JPEG or PNG, up to 5 MB.' },
  video: { accept: 'video/mp4,video/3gpp', words: 'An MP4 or 3GP video, up to 16 MB.' },
  document: { accept: 'application/pdf', words: 'A PDF, up to 16 MB.' },
};

const ICONS = { image: ImageIcon, video: Video, document: FileText, location: MapPin };

export function TemplateHeaderEditor({
  template,
  onChanged,
}: {
  template: ChannelTemplate;
  onChanged: (saved: ChannelTemplate) => void;
}) {
  const format = template.header_needed;
  if (!format) return null;
  const Icon = ICONS[format];
  return (
    <div className="space-y-2 rounded-control border border-line p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted">
        <Icon className="size-3.5" />
        {format === 'location' ? 'Place shown at the top' : `${capital(format)} shown at the top`}
        {template.header ? null : <span className="text-warning">· needed before it can be sent</span>}
      </p>
      {format === 'location' ? (
        <LocationHeader template={template} onChanged={onChanged} />
      ) : (
        <FileHeader template={template} format={format} onChanged={onChanged} />
      )}
    </div>
  );
}

function capital(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function FileHeader({
  template,
  format,
  onChanged,
}: {
  template: ChannelTemplate;
  format: Exclude<TemplateHeaderFormat, 'location'>;
  onChanged: (saved: ChannelTemplate) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const header = template.header;
  const path = `/messaging/templates/${template.public_id}/header/file`;
  // Keyed on what was saved, so a replaced file is fetched again.
  const preview = useFileUrl(header ? `${path}?v=${header.size}-${template.updated_at}` : null, {
    enabled: format !== 'document',
  });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return api.upload<ChannelTemplate>(path, form);
    },
    onSuccess: (saved) => {
      toast.success('Header saved', 'Every message sent with this template shows it.');
      onChanged(saved);
    },
  });

  return (
    <div className="space-y-2">
      {upload.error ? (
        <Alert tone="danger" title="Not saved">
          {upload.error instanceof ApiError ? upload.error.message : 'Could not reach the server.'}
        </Alert>
      ) : null}
      {header && preview.data ? (
        format === 'image' ? (
          <img src={preview.data} alt="" className="max-h-40 rounded-control border border-line" />
        ) : (
          <video src={preview.data} controls className="max-h-40 rounded-control border border-line" />
        )
      ) : null}
      {header ? (
        <p className="text-xs text-body">
          {header.filename}
          {header.size ? <span className="text-faint"> · {Math.max(1, Math.round(header.size / 1024))} KB</span> : null}
        </p>
      ) : null}
      <input
        ref={input}
        type="file"
        accept={ACCEPT[format].accept}
        className="hidden"
        aria-label={`Choose the ${format}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) upload.mutate(file);
        }}
      />
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" loading={upload.isPending} onClick={() => input.current?.click()}>
          <Upload className="size-3.5" />
          {header ? `Replace the ${format}` : `Choose the ${format}`}
        </Button>
        <span className="text-2xs text-faint">{ACCEPT[format].words}</span>
      </div>
    </div>
  );
}

function LocationHeader({
  template,
  onChanged,
}: {
  template: ChannelTemplate;
  onChanged: (saved: ChannelTemplate) => void;
}) {
  const saved: TemplateHeader | null = template.header;
  const [latitude, setLatitude] = useState(saved?.latitude?.toString() ?? '');
  const [longitude, setLongitude] = useState(saved?.longitude?.toString() ?? '');
  const [name, setName] = useState(saved?.name ?? '');
  const [address, setAddress] = useState(saved?.address ?? '');

  const lat = Number(latitude);
  const lng = Number(longitude);
  const valid =
    latitude.trim() !== '' &&
    longitude.trim() !== '' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180;

  const save = useMutation({
    mutationFn: () =>
      api.put<ChannelTemplate>(`/messaging/templates/${template.public_id}/header/location`, {
        latitude: lat,
        longitude: lng,
        name: name.trim(),
        address: address.trim(),
      }),
    onSuccess: (result) => {
      toast.success('Place saved', 'Every message sent with this template shows it.');
      onChanged(result);
    },
  });

  return (
    <div className="space-y-2">
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {save.error instanceof ApiError ? save.error.message : 'Could not reach the server.'}
        </Alert>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Latitude" inputMode="decimal" value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="17.3850" />
        <Input label="Longitude" inputMode="decimal" value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="78.4867" />
        <Input label="Name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="Head office" />
        <Input label="Address" maxLength={200} value={address} onChange={(e) => setAddress(e.target.value)} />
      </div>
      <Button variant="secondary" size="sm" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
        <MapPin className="size-3.5" />
        Save the place
      </Button>
    </div>
  );
}
